import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Check, 
  Calendar as CalendarIcon, 
  Clock, 
  Phone, 
  MessageSquare, 
  Mail,
  Video,
  Building2, 
  ShieldCheck, 
  AlertCircle, 
  FileText, 
  Flame, 
  XCircle, 
  ChevronLeft, 
  ChevronRight, 
  BadgeCheck,
  Rocket,
  Package,
  Hourglass,
  ArrowRight,
  CheckCircle2,
  Zap,
  Tag,
  Truck,
  Search,
  Users,
  DollarSign,
  Ban,
  Ruler,
  Pencil,
  CalendarDays
} from "lucide-react";
import { BsWhatsapp } from "react-icons/bs";
import { toast } from "sonner";
import { 
  format, 
  addMonths, 
  subMonths, 
  isSameDay, 
  isBefore, 
  isAfter, 
  startOfToday, 
  startOfMonth, 
  endOfMonth, 
  endOfYear, 
  eachDayOfInterval, 
  getDay, 
  addYears 
} from "date-fns";
import { cn } from "../Lib/Cn";
import { getUploadUrl, getImageRoot } from "../Utils/Utilities";
import externalAPI from "./ExternalAPI";
import { TimeInputWithDialog } from "../Components/Common/TimePickerWatch";
import { Button } from "../Components/UI/button";

const DECISION_TYPES = {
  READY: "ready",
  DELAY: "delay",
  DROP: "drop",
};

// 1-Tap Quick Action Presets with Icons
const QUICK_NOTES = {
  READY: [
    { text: "Please send proforma invoice", icon: FileText },
    { text: "Call me to finalize terms", icon: Phone },
    { text: "Need urgent delivery", icon: Truck },
    { text: "Chat with me on WhatsApp", icon: BsWhatsapp }
  ],
  DELAY: [
    { text: "Budget approval in progress", icon: Clock },
    { text: "Evaluating technical alternatives", icon: Search },
    { text: "Revisit with team next week", icon: CalendarDays },
    { text: "Final decision pending management", icon: Users }
  ],
  DROP: [
    { text: "Price is higher than budget", icon: DollarSign },
    { text: "Purchased from another vendor", icon: Building2 },
    { text: "Project cancelled / postponed", icon: Ban },
    { text: "Specs did not match requirement", icon: Ruler }
  ],
  QUALIFY_HOT: [
    { text: "Need delivery within 3 days", icon: Zap },
    { text: "Please send catalog & best price", icon: FileText },
    { text: "Call me in the afternoon", icon: Phone }
  ],
  QUALIFY_WARM: [
    { text: "Share brochure on WhatsApp", icon: BsWhatsapp },
    { text: "Planning for next month's requirement", icon: CalendarDays }
  ],
  SCHEDULE: [
    { text: "Quotation & commercial discussion", icon: FileText },
    { text: "Technical specifications & demo", icon: Video },
    { text: "Delivery timeline & payment terms", icon: Clock }
  ]
};

const format24to12 = (timeValue) => {
  if (!timeValue) return "";
  const [hours, minutes] = timeValue.split(":").map(Number);
  let displayHour = hours;
  let ampm = "AM";
  if (hours === 0) {
    displayHour = 12;
    ampm = "AM";
  } else if (hours === 12) {
    displayHour = 12;
    ampm = "PM";
  } else if (hours > 12) {
    displayHour = hours - 12;
    ampm = "PM";
  }
  return `${displayHour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

export default function CustomerConnect() {
  const [searchParams] = useSearchParams();

  // Read URL params: support t/a and tid/aid
  const threadID = searchParams.get("t") || searchParams.get("tid");
  const agencyID = searchParams.get("a") || searchParams.get("aid");

  // Determine Touchpoint Mode from URL
  const rawTyp = (searchParams.get("typ") || "").toLowerCase();
  const rawM = searchParams.get("m");
  const isScheduleMode = rawTyp === "schedule" || rawTyp === "connect" || rawM === "6";
  const isQualifyMode = rawTyp === "qualify" || rawTyp === "verification" || rawM === "3";

  // Loading & Error States
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Loaded DB Data
  const [agencyInfo, setAgencyInfo] = useState({
    agencyName: "",
    countryID: 0,
    googleLink: "",
    logo: "",
  });

  const [threadInfo, setThreadInfo] = useState({
    threadID: null,
    connID: null,
    reference: "",
    mainProduct: "",
    mainQty: "",
    bizProspect: "",
    threadTypeName: "",
    sinceDT: null,
    dueDT: null,
    currStage: "",
    contactName: "",
    bizName: "",
    connMobile: "",
    connEmail: "",
    pocUserID: null,
    pocUserName: "",
    pocUserMobile: "",
    pocUserDesignation: "",
    fdbkScore: 0,
    fdbkNotes: "",
    failureReason: "",
  });

  // ================= Form State: Decision Pull (#2) =================
  const [decision, setDecision] = useState(null);
  const [delayTimeline, setDelayTimeline] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [dropReasonOptions, setDropReasonOptions] = useState([
    "Price is higher than budget",
    "Purchased from competitor",
    "Project postponed / Cancelled",
    "Missing required specification",
    "Timeline / Delivery didn't match",
  ]);
  const [dropReason, setDropReason] = useState("");
  const [competitorName, setCompetitorName] = useState("");

  // ================= Form State: Lead Qualification (#3) =================
  const [isRequirementAccurate, setIsRequirementAccurate] = useState(true);
  const [buyingUrgency, setBuyingUrgency] = useState("Immediate (1-3 Days)");
  const [preferredContact, setPreferredContact] = useState("WhatsApp");
  const [expectedVolume, setExpectedVolume] = useState("Standard Batch");
  const [qualifyDropReason, setQualifyDropReason] = useState("Inquired by mistake");

  // ================= Form State: Schedule / Next Time to Connect (#6) =================
  const [currentMonth, setCurrentMonth] = useState(startOfToday());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime24, setSelectedTime24] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [meetingMode, setMeetingMode] = useState("Phone Call");
  const [meetingTopic, setMeetingTopic] = useState("");

  // Common Remarks / Notes
  const [remarks, setRemarks] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Calendar Day Generation (Fixed 6-row / 42-cell grid)
  const today = startOfToday();
  const maxDate = endOfYear(addYears(today, 1));
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOffset = getDay(monthStart);
  const trailingOffset = 42 - (startDayOffset + days.length);

  const handlePrevMonth = () => {
    if (isBefore(endOfMonth(subMonths(currentMonth, 1)), today)) return;
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    if (isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)) return;
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleDateSelect = (day) => {
    if (isBefore(day, today) || isAfter(day, maxDate)) return;
    setSelectedDate(day);
  };

  const handleTimeChange = (newTime24) => {
    setSelectedTime24(newTime24);
    setSelectedSlot(format24to12(newTime24));
  };

  // Parse Thread Type Name from Configs.ThreadTypes
  const parseThreadTypeName = (threadTypesRaw, typeID) => {
    if (!threadTypesRaw || !typeID) return "";
    try {
      const list = typeof threadTypesRaw === "string" ? JSON.parse(threadTypesRaw) : threadTypesRaw;
      if (Array.isArray(list)) {
        const matched = list.find(
          (item) => item.TID === typeID || item.TypeID === typeID || item.id === typeID
        );
        return matched?.Name || matched?.TypeName || matched?.label || "";
      }
    } catch (e) {
      console.error("Error parsing thread types:", e);
    }
    return "";
  };

  // Parse rejection / failure reasons based on Configs.RejRsns and Thread TypeID
  const parseRejectionReasons = (rejRsns, typeID) => {
    if (!rejRsns) return [];
    try {
      const masterData = typeof rejRsns === "string" ? JSON.parse(rejRsns) : rejRsns;
      let reasonsList = [];
      if (Array.isArray(masterData)) {
        const threadConfig = masterData.find(item => item.TID === typeID || item.TypeID === typeID) || masterData[0];
        if (threadConfig?.Rsns) {
          const parsedRsns = typeof threadConfig.Rsns === "string" ? JSON.parse(threadConfig.Rsns) : threadConfig.Rsns;
          if (Array.isArray(parsedRsns)) {
            reasonsList = parsedRsns.map(r => (typeof r === "object" && r !== null ? (r.R || r.Reason || r.label || r.value) : r));
          }
        }
      }
      return reasonsList.filter(Boolean);
    } catch (error) {
      console.error("Error parsing failure reasons:", error);
      return [];
    }
  };

  // 1. Fetch Thread & Agency Data on Mount
  useEffect(() => {
    const fetchTouchpointData = async () => {
      if (!threadID || !agencyID) {
        setIsLoading(false);
        setLoadError("Invalid or missing link parameters. Please check your link.");
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const payload = {
          agencyID: Number(agencyID),
          threadID: Number(threadID),
        };

        const apiType = isScheduleMode ? "SCHEDULE" : isQualifyMode ? "QUALIFY" : "DECISION";
        const response = await externalAPI.getFeedbackDetails(payload, apiType);

        if (response && response.parsedDataSet) {
          const { Table1, Table2 } = response.parsedDataSet;
          const agn = Table1?.[0] || {};
          const thr = Table2?.[0] || {};

          if (!thr || !thr.ThreadID) {
            setLoadError("Inquiry details not found or link has expired.");
            setIsLoading(false);
            return;
          }

          const rawLogo = agn.Logo || agn.LogoPath || agn.logo || "";
          const resolvedLogoUrl = rawLogo
            ? (rawLogo.startsWith("http") ? rawLogo : getUploadUrl(rawLogo.startsWith("Uploads/") ? rawLogo : `Uploads/${rawLogo}`))
            : "";

          setAgencyInfo({
            agencyName: agn.AgencyName || "Our Company",
            countryID: agn.CountryID || 0,
            googleLink: agn.GoogleLink || "",
            logo: resolvedLogoUrl,
          });

          // Parse dynamic failure reasons from Configs
          const parsedReasons = parseRejectionReasons(agn.RejRsns, thr.TypeID);
          if (parsedReasons && parsedReasons.length > 0) {
            setDropReasonOptions(parsedReasons);
          }

          const parsedThrType = parseThreadTypeName(agn.ThreadTypes, thr.TypeID);
          const cleanProduct = thr.MainProduct != null ? String(thr.MainProduct).trim() : "";
          const cleanQty = thr.MainQty != null ? String(thr.MainQty).trim() : "";
          const cleanRef = thr.Reference != null && String(thr.Reference).trim() ? String(thr.Reference).trim() : `#${thr.ThreadID}`;

          setThreadInfo({
            threadID: thr.ThreadID,
            connID: thr.ConnID,
            reference: cleanRef,
            mainProduct: cleanProduct,
            mainQty: cleanQty,
            bizProspect: thr.BizProspect != null ? String(thr.BizProspect).trim() : "",
            threadTypeName: parsedThrType,
            sinceDT: thr.SinceDT,
            dueDT: thr.DueDT,
            currStage: thr.CurrStage != null ? String(thr.CurrStage).trim() : "",
            contactName: thr.ContactName != null ? String(thr.ContactName).trim() : "",
            bizName: thr.BizName != null ? String(thr.BizName).trim() : "",
            connMobile: thr.ConnMobile != null ? String(thr.ConnMobile).trim() : "",
            connEmail: thr.ConnEmail != null ? String(thr.ConnEmail).trim() : "",
            pocUserID: thr.POCUserID,
            pocUserName: thr.POCUserName != null && String(thr.POCUserName).trim() ? String(thr.POCUserName).trim() : "Sales Executive",
            pocUserMobile: thr.POCUserMobile != null ? String(thr.POCUserMobile).trim() : "",
            pocUserDesignation: thr.POCUserDesignation != null && String(thr.POCUserDesignation).trim() ? String(thr.POCUserDesignation).trim() : "Account Manager",
            fdbkScore: thr.FdbkScore || 0,
            fdbkNotes: thr.FdbkNotes != null ? String(thr.FdbkNotes) : "",
            failureReason: thr.FailureReason != null ? String(thr.FailureReason) : "",
          });

          const defaultTopic = cleanProduct
            ? `Discussion regarding ${cleanProduct}`
            : parsedThrType
            ? `Discussion regarding ${parsedThrType}`
            : `Discussion regarding Inquiry ${cleanRef}`;

          setMeetingTopic(defaultTopic);
        } else {
          setLoadError(response?.ErrorMessage || "Unable to load details. Please try again.");
        }
      } catch (err) {
        console.error("Error loading touchpoint data:", err);
        setLoadError("Unable to connect to the server. Please check your internet connection.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTouchpointData();
  }, [threadID, agencyID, isQualifyMode, isScheduleMode]);

  // Calculate default postpone date
  const getCalculatedDate = (choice, pickedDate) => {
    if (choice === "Custom Date" && pickedDate) return pickedDate;
    const t = new Date();
    if (choice === "In 1 Week") t.setDate(t.getDate() + 7);
    else if (choice === "In 2 Weeks") t.setDate(t.getDate() + 14);
    else if (choice === "Next Month") t.setDate(t.getDate() + 30);
    else t.setDate(t.getDate() + 7);
    return t.toISOString().split("T")[0];
  };

  // 2. Submit Handler
  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!threadID || !agencyID || !threadInfo.connID) {
      toast.error("Missing inquiry identification. Please refresh the page.");
      return;
    }

    let fdbkScore = 1;
    let rejReason = "";
    let combinedNotes = "";
    let submitType = isScheduleMode ? "SCHEDULE" : isQualifyMode ? "QUALIFY" : "DECISION";

    if (isScheduleMode) {
      if (!selectedDate || !selectedSlot) {
        toast.error("Please select a date and time slot.");
        return;
      }

      if (meetingMode === "Phone Call") fdbkScore = 1;
      else if (meetingMode === "WhatsApp Call") fdbkScore = 2;
      else if (meetingMode === "Google Meet / Zoom") fdbkScore = 3;
      else fdbkScore = 1;

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const timeStr = selectedTime24 ? `${selectedTime24}:00` : "10:00:00";
      rejReason = `${dateStr} ${timeStr}`;

      combinedNotes = `Mode: ${meetingMode} | Time: ${selectedSlot} | Topic: ${meetingTopic || threadInfo.mainProduct || threadInfo.threadTypeName || "Meeting"}${remarks ? ` | Note: ${remarks.trim()}` : ""}`;
    } else if (isQualifyMode) {
      if (isRequirementAccurate) {
        const isHot = buyingUrgency.startsWith("Immediate");
        fdbkScore = isHot ? 1 : 2;
        combinedNotes = `Urgency: ${buyingUrgency} | Contact Via: ${preferredContact} | Volume: ${expectedVolume}${remarks ? ` | Note: ${remarks.trim()}` : ""}`;
        rejReason = "";
      } else {
        fdbkScore = 3;
        rejReason = qualifyDropReason;
        combinedNotes = remarks ? `Note: ${remarks.trim()}` : "";
      }
    } else {
      if (decision === DECISION_TYPES.READY) {
        fdbkScore = 1;
        combinedNotes = `Status: Ready to Proceed${remarks ? ` | Note: ${remarks.trim()}` : ""}`;
        rejReason = "";
      } else if (decision === DECISION_TYPES.DELAY) {
        fdbkScore = 2;
        const targetDate = getCalculatedDate(delayTimeline, customDate);
        rejReason = targetDate;
        combinedNotes = `Revisit Timeframe: ${delayTimeline} (${targetDate})${remarks ? ` | Note: ${remarks.trim()}` : ""}`;
      } else if (decision === DECISION_TYPES.DROP) {
        fdbkScore = 3;
        rejReason = competitorName ? `${dropReason} (Competitor: ${competitorName.trim()})` : dropReason;
        combinedNotes = remarks ? `Note: ${remarks.trim()}` : "";
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        agencyID: Number(agencyID),
        connID: Number(threadInfo.connID),
        threadID: Number(threadID),
        fdbkScore: fdbkScore,
        fdbkNotes: combinedNotes,
        rejReason: rejReason,
        type: submitType,
      };

      const response = await externalAPI.setFeedbackDetails(payload, submitType);

      if (response && response.parsedDataSet) {
        const resultTable = response.parsedDataSet?.Table?.[0] || response.parsedDataSet?.Table1?.[0];
        const errCode = resultTable?.FnErrCode;

        if (errCode === -1 || errCode === "-1" || !response.HasError) {
          setSubmittedData({
            isScheduleMode,
            isQualifyMode,
            selectedDate,
            selectedSlot,
            meetingMode,
            meetingTopic,
            isRequirementAccurate,
            buyingUrgency,
            preferredContact,
            expectedVolume,
            qualifyDropReason,
            decision,
            delayTimeline,
            calculatedDate: getCalculatedDate(delayTimeline, customDate),
            dropReason,
            remarks,
          });
          setSubmitted(true);
          toast.success("Thank you! Your response has been recorded.");
        } else {
          toast.error(resultTable?.ErrMsg || "Failed to save request. Please try again.");
        }
      } else {
        setSubmittedData({
          isScheduleMode,
          isQualifyMode,
          selectedDate,
          selectedSlot,
          meetingMode,
          meetingTopic,
          isRequirementAccurate,
          buyingUrgency,
          preferredContact,
          expectedVolume,
          qualifyDropReason,
          decision,
          delayTimeline,
          calculatedDate: getCalculatedDate(delayTimeline, customDate),
          dropReason,
          remarks,
        });
        setSubmitted(true);
        toast.success("Request recorded successfully.");
      }
    } catch (error) {
      console.error("Error submitting response:", error);
      toast.error("Something went wrong while submitting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const greetingName = threadInfo.contactName || threadInfo.bizName || "Valued Customer";
  const hasProduct = Boolean(threadInfo.mainProduct && threadInfo.mainProduct.trim());

  const renderNotesAndSubmit = (activeNotes = [], submitLabel = "Submit Decision") => {
    const handleAddNote = (noteText) => {
      setRemarks((prev) => {
        if (!prev) return noteText;
        if (prev.includes(noteText)) return prev;
        return `${prev.trim()}, ${noteText}`;
      });
    };

    return (
      <div className="space-y-3 pt-3 border-t border-[var(--sahi-border-color)] animate-in fade-in duration-150">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-[var(--text-color)] flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-sahi-primary-color" />
            Any specific instructions or notes? <span className="text-[var(--sub-text-color)] font-normal">(Optional)</span>
          </label>
          {activeNotes.length > 0 && (
            <span className="text-[10px] text-[var(--sub-text-color)] font-medium hidden sm:inline">1-tap chips below</span>
          )}
        </div>

        {/* 1-Tap Quick Notes Suggestions */}
        {activeNotes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {activeNotes.map((chip) => {
              const Icon = chip.icon;
              return (
                <button
                  key={chip.text}
                  type="button"
                  onClick={() => handleAddNote(chip.text)}
                  className="text-xs px-2.5 py-1.5 rounded-lg bg-white border border-[var(--sahi-border-color)] hover:border-sahi-primary-color hover:text-sahi-primary-color text-[var(--text-color)] transition-all font-medium flex items-center gap-1.5 shadow-2xs active:scale-95 group"
                >
                  <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--sub-text-color)] group-hover:text-sahi-primary-color" />
                  <span>{chip.text}</span>
                </button>
              );
            })}
          </div>
        )}

        <textarea
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Type here or tap any suggestion above..."
          className="w-full px-3 py-2 border border-[var(--sahi-border-color)] rounded-lg text-xs sm:text-sm text-[var(--text-color)] bg-white placeholder-gray-400 focus:outline-none focus:border-sahi-primary-color shadow-2xs resize-none transition-all"
        />

        {/* Primary Submit Action */}
        <div className="pt-1">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-11 font-semibold text-sm rounded-lg bg-sahi-primary-color hover:opacity-95 active:scale-[0.99] text-white shadow-xs transition-all flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                <span>Recording response...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 stroke-[2.5]" />
                <span>{submitLabel}</span>
                <ArrowRight className="w-4 h-4 ml-0.5 opacity-80" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--bg-grey)] text-[var(--text-color)] font-sans flex flex-col">
      {/* Top Header Bar with Agency Logo & Branding (Pure White, Sahi style) */}
      <header className="sticky top-0 z-40 w-full bg-white border-b border-[var(--sahi-border-color)] px-4 sm:px-6 py-2.5 sm:py-3 shadow-2xs">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          {/* Agency Logo & Name */}
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={agencyInfo.logo || getImageRoot("Images", "SahiLogo.png")}
              alt={agencyInfo.agencyName || "SahiClub"}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-contain border border-[var(--sahi-border-color)] bg-white p-0.5 shadow-2xs shrink-0"
              onError={(e) => {
                e.currentTarget.src = getImageRoot("Images", "SahiLogo.png");
              }}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-md sm:text-base font-semibold text-[var(--text-color)] truncate">
                  {agencyInfo.agencyName || "SahiCRM Partner"}
                </span>
              </div>
            </div>
          </div>

          {/* Quick WhatsApp Action with Executive */}
          {threadInfo.pocUserName && threadInfo.pocUserMobile && (
            <div className="flex items-center shrink-0">
              <a
                href={`https://wa.me/${threadInfo.pocUserMobile.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-sahi-green hover:bg-sahi-green-light bg-sahi-green-extra-light border border-sahi-green/20 rounded-lg p-1.5 transition-colors shadow-2xs"
                title={`Chat with ${threadInfo.pocUserName} on WhatsApp`}
              >
                <BsWhatsapp className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="w-full max-w-2xl mx-auto p-4 sm:p-5 flex-1 flex flex-col">
        {/* Loading State */}
        {isLoading && (
          <div className="p-16 flex flex-col items-center justify-center text-center my-auto">
            <div className="w-8 h-8 border-2 border-[var(--sahi-border-color)] border-t-sahi-primary-color rounded-full animate-spin mb-3" />
            <div className="text-sm font-semibold text-[var(--text-color)]">Loading inquiry details...</div>
            <div className="text-xs text-[var(--sub-text-color)] mt-0.5">Please wait a moment.</div>
          </div>
        )}

        {/* Error State */}
        {!isLoading && loadError && (
          <div className="p-6 my-auto bg-white rounded-xl border border-[var(--sahi-border-color)] shadow-2xs flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center text-sahi-red mb-2.5">
              <AlertCircle className="h-5 w-5" />
            </div>
            <h2 className="text-sm font-semibold text-[var(--text-color)]">Unable to Load Inquiry</h2>
            <p className="text-xs text-[var(--sub-text-color)] mt-1 max-w-md">{loadError}</p>
          </div>
        )}

        {/* Content Body */}
        {!isLoading && !loadError && (
          <div className="space-y-4">
            {submitted ? (
              /* Success / Thank You Screen */
              <div className="py-6 flex flex-col items-center text-center animate-in fade-in duration-200">
                <div className="w-14 h-14 rounded-xl bg-sahi-green-extra-light border border-sahi-green/30 flex items-center justify-center text-sahi-green mb-3 shadow-2xs">
                  <BadgeCheck className="h-8 w-8" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-[var(--text-color)]">
                  {isScheduleMode ? "Event Scheduled" : `Thank You, ${greetingName}!`}
                </h2>
                <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1 max-w-md leading-relaxed">
                  {isScheduleMode
                    ? `Your connect has been scheduled with ${threadInfo.pocUserName}.`
                    : `Your response has been saved and shared with ${threadInfo.pocUserName}.`}
                </p>

                {/* Confirmed Summary Card */}
                <div className="w-full mt-4 bg-white border border-[var(--sahi-border-color)] rounded-xl p-4 text-left shadow-2xs space-y-3">
                  <div className="text-xs font-semibold text-[var(--sub-text-color)] flex items-center justify-between">
                    <span>Confirmed Summary</span>
                    <span className="text-sahi-green bg-sahi-green-extra-light px-2 py-0.5 rounded text-[10px] font-semibold border border-sahi-green/20 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Synced
                    </span>
                  </div>

                  {isScheduleMode ? (
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-[var(--text-color)]">
                        <CalendarIcon className="w-4 h-4 text-sahi-primary-color shrink-0" />
                        <span className="font-semibold text-sm">
                          {submittedData?.selectedDate ? format(submittedData.selectedDate, "EEEE, MMMM d, yyyy") : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[var(--text-color)]">
                        <Clock className="w-4 h-4 text-sahi-primary-color shrink-0" />
                        <span>
                          <strong className="font-semibold text-[var(--text-color)]">{submittedData?.selectedSlot}</strong> via {submittedData?.meetingMode}
                        </span>
                      </div>
                      {submittedData?.meetingTopic && (
                        <div className="pt-2 border-t border-gray-100 text-[var(--sub-text-color)]">
                          <span className="font-semibold text-[var(--text-color)]">Topic:</span> {submittedData.meetingTopic}
                        </div>
                      )}
                    </div>
                  ) : isQualifyMode ? (
                    submittedData?.isRequirementAccurate ? (
                      <div className="space-y-2 text-xs">
                        <div className="font-semibold text-sahi-green flex items-center gap-1.5">
                          <Check className="h-4 w-4 stroke-[2.5]" /> {hasProduct ? "Requirement Verified & Confirmed" : "Inquiry Verified & Confirmed"}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-[var(--text-color)]">
                          <div className="bg-sahi-disable p-2 rounded-lg border border-[var(--sahi-border-color)]">
                            <span className="text-[10px] text-[var(--sub-text-color)] block font-medium">Urgency</span>
                            <span className="font-semibold text-[var(--text-color)]">{submittedData.buyingUrgency}</span>
                          </div>
                          <div className="bg-sahi-disable p-2 rounded-lg border border-[var(--sahi-border-color)]">
                            <span className="text-[10px] text-[var(--sub-text-color)] block font-medium">Preferred Contact</span>
                            <span className="font-semibold text-[var(--text-color)]">{submittedData.preferredContact}</span>
                          </div>
                          <div className="bg-sahi-disable p-2 rounded-lg border border-[var(--sahi-border-color)]">
                            <span className="text-[10px] text-[var(--sub-text-color)] block font-medium">Expected Volume</span>
                            <span className="font-semibold text-[var(--text-color)]">{submittedData.expectedVolume}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5 text-xs">
                        <div className="font-semibold text-sahi-red flex items-center gap-1.5">
                          <XCircle className="h-4 w-4" /> Dropped / Inquired by Mistake
                        </div>
                        <div className="text-[var(--text-color)]">
                          Reason: <span className="font-semibold">{submittedData.qualifyDropReason}</span>
                        </div>
                      </div>
                    )
                  ) : (
                    submittedData?.decision === DECISION_TYPES.READY ? (
                      <div className="space-y-1 text-xs">
                        <div className="font-semibold text-sahi-green flex items-center gap-1.5 text-sm">
                          <Check className="h-4 w-4 stroke-[2.5]" /> Ready to Proceed
                        </div>
                        <div className="text-[var(--sub-text-color)]">
                          Status: <span className="font-semibold text-[var(--text-color)]">Finalized — Ready to place order & start</span>
                        </div>
                      </div>
                    ) : submittedData?.decision === DECISION_TYPES.DELAY ? (
                      <div className="space-y-1 text-xs">
                        <div className="font-semibold text-sahi-orange flex items-center gap-1.5 text-sm">
                          <Clock className="h-4 w-4" /> Need More Time (Postponed)
                        </div>
                        <div className="text-[var(--sub-text-color)]">
                          Revisit Date: <span className="font-semibold text-[var(--text-color)]">{submittedData.calculatedDate}</span> ({submittedData.delayTimeline})
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-xs">
                        <div className="font-semibold text-sahi-red flex items-center gap-1.5 text-sm">
                          <AlertCircle className="h-4 w-4" /> Not Proceeding
                        </div>
                        <div className="text-[var(--sub-text-color)]">
                          Reason: <span className="font-semibold text-[var(--text-color)]">{submittedData.dropReason}</span>
                        </div>
                      </div>
                    )
                  )}

                  {submittedData?.remarks && (
                    <div className="pt-2 border-t border-gray-100 text-xs text-[var(--sub-text-color)]">
                      <span className="font-semibold text-[var(--text-color)]">Your Instructions:</span> "{submittedData.remarks}"
                    </div>
                  )}
                </div>

                {/* Direct Action Buttons */}
                {threadInfo.pocUserMobile && (
                  <div className="mt-4 flex items-center justify-center w-full">
                    <a
                      href={`https://wa.me/${threadInfo.pocUserMobile.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-sahi-green text-white text-xs font-semibold hover:opacity-95 transition-all shadow-2xs active:scale-95"
                    >
                      <BsWhatsapp className="w-3.5 h-3.5" />
                      Chat with {threadInfo.pocUserName}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Welcoming Header */}
                <div className="space-y-1">
                  <h1 className="text-lg sm:text-xl font-bold text-[var(--text-color)] tracking-tight">
                    {isScheduleMode
                      ? "Schedule a Quick Connect"
                      : isQualifyMode
                      ? "Inquiry Verification"
                      : "Inquiry Decision & Next Step"}
                  </h1>
                  <p className="text-[13px] sm:text-sm text-[var(--sub-text-color)] leading-relaxed">
                    Hi <span className="font-semibold text-[var(--text-color)]">{greetingName}</span>,{" "}
                    {isScheduleMode ? (
                      hasProduct ? (
                        <>
                          please choose a convenient date & time so{" "}
                          <span className="font-semibold text-[var(--text-color)]">{threadInfo.pocUserName || "our team"}</span>{" "}
                          can assist you with your requirement for{" "}
                          <span className="font-semibold text-[var(--text-color)]">
                            {threadInfo.mainProduct}
                            {threadInfo.mainQty ? ` (${threadInfo.mainQty})` : ""}
                          </span>.
                        </>
                      ) : threadInfo.threadTypeName ? (
                        <>
                          please choose a convenient date & time so{" "}
                          <span className="font-semibold text-[var(--text-color)]">{threadInfo.pocUserName || "our team"}</span>{" "}
                          can assist you regarding your{" "}
                          <span className="font-semibold text-[var(--text-color)]">{threadInfo.threadTypeName}</span> inquiry.
                        </>
                      ) : (
                        `please choose a convenient date & time so ${threadInfo.pocUserName || "our team"} can assist you with your inquiry.`
                      )
                    ) : isQualifyMode ? (
                      hasProduct ? (
                        <>
                          please confirm your requirement details for{" "}
                          <span className="font-semibold text-[var(--text-color)]">
                            {threadInfo.mainProduct}
                            {threadInfo.mainQty ? ` (${threadInfo.mainQty})` : ""}
                          </span>{" "}
                          so we can assist you promptly.
                        </>
                      ) : threadInfo.threadTypeName ? (
                        <>
                          please confirm your{" "}
                          <span className="font-semibold text-[var(--text-color)]">{threadInfo.threadTypeName}</span> inquiry details so we can assist you promptly.
                        </>
                      ) : (
                        "please confirm your inquiry details so we can assist you promptly."
                      )
                    ) : (
                      hasProduct ? (
                        <>
                          please let us know how you would like to proceed with your requirement for{" "}
                          <span className="font-semibold text-[var(--text-color)]">
                            {threadInfo.mainProduct}
                            {threadInfo.mainQty ? ` (${threadInfo.mainQty})` : ""}
                          </span>.
                        </>
                      ) : threadInfo.threadTypeName ? (
                        <>
                          please let us know how you would like to proceed with your{" "}
                          <span className="font-semibold text-[var(--text-color)]">{threadInfo.threadTypeName}</span> inquiry.
                        </>
                      ) : (
                        "please let us know how you would like to proceed with this inquiry."
                      )
                    )}
                  </p>
                </div>

                {/* Main Interactive Form */}
                <form onSubmit={handleSubmit} className="space-y-4">

                {/* ================= TOUCHPOINT 6: SCHEDULE ================= */}
                {isScheduleMode ? (
                  <div className="space-y-3.5">
                    {/* Meeting Mode Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-color)] mb-2">
                        How would you like to connect? <span className="text-sahi-red">*</span>
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: "Phone Call", label: "Phone Call", icon: Phone },
                          { id: "WhatsApp Call", label: "WhatsApp", icon: BsWhatsapp },
                          { id: "Google Meet / Zoom", label: "Video Meet", icon: Video },
                        ].map((m) => {
                          const Icon = m.icon;
                          const isSel = meetingMode === m.id;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => setMeetingMode(m.id)}
                              className={cn(
                                "p-2.5 rounded-lg border text-center transition-all flex flex-col items-center justify-center gap-1.5 text-xs font-medium active:scale-[0.98]",
                                isSel
                                  ? "border-2 border-sahi-primary-color bg-sahi-dark-blue-light text-sahi-primary-color font-semibold shadow-2xs"
                                  : "border-[var(--sahi-border-color)] bg-white text-[var(--text-color)] hover:bg-gray-50"
                              )}
                            >
                              <Icon className="h-4 w-4" />
                              <span>{m.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Month Calendar Grid */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-color)] mb-2">
                        Select Preferred Date <span className="text-sahi-red">*</span>
                      </label>

                      <div className="w-full bg-white border border-[var(--sahi-border-color)] rounded-xl p-3.5 shadow-2xs">
                        {/* Month Header */}
                        <div className="flex items-center justify-between mb-3 relative">
                          <button
                            type="button"
                            onClick={handlePrevMonth}
                            disabled={isBefore(endOfMonth(subMonths(currentMonth, 1)), today)}
                            className="p-1 rounded-md border border-[var(--sahi-border-color)] bg-white hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[var(--text-color)] transition-colors z-10"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>

                          <div className="font-semibold text-[var(--text-color)] text-xs sm:text-sm text-center absolute inset-x-0 pointer-events-none">
                            <span>{format(currentMonth, "MMMM yyyy")}</span>
                          </div>

                          <button
                            type="button"
                            onClick={handleNextMonth}
                            disabled={isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)}
                            className="p-1 rounded-md border border-[var(--sahi-border-color)] bg-white hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[var(--text-color)] transition-colors z-10"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Day of Week Labels */}
                        <div className="grid grid-cols-7 text-center text-[10px] font-medium text-[var(--sub-text-color)] mb-1.5">
                          <span>Sun</span>
                          <span>Mon</span>
                          <span>Tue</span>
                          <span>Wed</span>
                          <span>Thu</span>
                          <span>Fri</span>
                          <span>Sat</span>
                        </div>

                        {/* Days Grid */}
                        <div className="grid grid-cols-7 gap-1 text-center">
                          {Array.from({ length: startDayOffset }).map((_, i) => (
                            <div key={`empty-${i}`} className="h-7 sm:h-8" />
                          ))}

                          {days.map((day) => {
                            const isPast = isBefore(day, today);
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isTodayDate = isSameDay(day, today);
                            const isSunday = getDay(day) === 0;

                            return (
                              <button
                                key={day.toISOString()}
                                type="button"
                                disabled={isPast || isSunday}
                                onClick={() => handleDateSelect(day)}
                                className={cn(
                                  "h-7 sm:h-8 w-full rounded-md flex items-center justify-center text-xs font-medium transition-all relative active:scale-95",
                                  isSelected && "bg-sahi-primary-color text-white font-semibold shadow-2xs",
                                  !isSelected && !isPast && !isSunday && "hover:bg-gray-100 text-[var(--text-color)] bg-white border border-gray-100",
                                  (isPast || isSunday) && "text-gray-300 cursor-not-allowed bg-transparent",
                                  isTodayDate && !isSelected && "border border-sahi-primary-color font-semibold text-sahi-primary-color bg-white"
                                )}
                              >
                                {format(day, "d")}
                              </button>
                            );
                          })}

                          {Array.from({ length: trailingOffset }).map((_, i) => (
                            <div key={`empty-end-${i}`} className="h-7 sm:h-8" />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Time Picker Row with TimeInputWithDialog */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-color)] mb-1.5">
                        Select Preferred Time <span className="text-sahi-red">*</span>
                      </label>

                      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <TimeInputWithDialog
                          value={selectedTime24}
                          onChange={handleTimeChange}
                          disabled={!selectedDate}
                          className="w-full sm:w-40 bg-white border border-[var(--sahi-border-color)] rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-semibold shadow-2xs"
                        />

                        {/* Quick Preset Buttons */}
                        <div className="flex items-center gap-1.5 flex-1">
                          {[
                            { label: "11:00 AM", t24: "11:00" },
                            { label: "03:00 PM", t24: "15:00" },
                            { label: "05:30 PM", t24: "17:30" },
                          ].map((p) => (
                            <button
                              key={p.t24}
                              type="button"
                              disabled={!selectedDate}
                              onClick={() => handleTimeChange(p.t24)}
                              className={cn(
                                "flex-1 py-1.5 px-2 rounded-lg border text-xs font-medium transition-all text-center active:scale-95",
                                selectedTime24 === p.t24
                                  ? "bg-sahi-primary-color text-white border-sahi-primary-color font-semibold shadow-2xs"
                                  : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              )}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Discussion Topic */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-color)] mb-1">
                        Discussion Topic <span className="text-[var(--sub-text-color)] font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={meetingTopic}
                        onChange={(e) => setMeetingTopic(e.target.value)}
                        placeholder="e.g. Quotation review, product specifications..."
                        className="w-full px-3 py-2 border border-[var(--sahi-border-color)] rounded-lg text-xs sm:text-sm text-[var(--text-color)] bg-white placeholder-gray-400 focus:outline-none focus:border-sahi-primary-color shadow-2xs"
                      />
                    </div>

                    {/* Inline Notes & Submit when Date and Time are selected */}
                    {selectedDate && selectedSlot && (
                      <div className="pt-2">
                        {renderNotesAndSubmit(QUICK_NOTES.SCHEDULE, "Confirm & Schedule Call")}
                      </div>
                    )}
                  </div>
                ) : isQualifyMode ? (
                  /* ================= TOUCHPOINT 3: LEAD QUALIFICATION ================= */
                  <div className="space-y-3.5">
                    {/* Step 1: Is Requirement / Inquiry Accurate? */}
                    <div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <button
                          type="button"
                          onClick={() => setIsRequirementAccurate(true)}
                          className={cn(
                            "p-3 rounded-xl border text-left transition-all flex items-center justify-between active:scale-[0.99]",
                            isRequirementAccurate
                              ? "border-2 border-sahi-green bg-sahi-green-extra-light shadow-2xs"
                              : "border-[var(--sahi-border-color)] bg-white hover:bg-gray-50"
                          )}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-sahi-green-light text-sahi-green flex items-center justify-center shrink-0">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="font-semibold text-xs sm:text-sm text-[var(--text-color)]">Yes, Accurate</div>
                              <div className="text-[11px] text-[var(--sub-text-color)]">{hasProduct ? "I need this product" : "I am interested"}</div>
                            </div>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setIsRequirementAccurate(false)}
                          className={cn(
                            "p-3 rounded-xl border text-left transition-all flex items-center justify-between active:scale-[0.99]",
                            !isRequirementAccurate
                              ? "border-2 border-sahi-red bg-sahi-red-light/10 shadow-2xs"
                              : "border-[var(--sahi-border-color)] bg-white hover:bg-gray-50"
                          )}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-sahi-red-light text-sahi-red flex items-center justify-center shrink-0">
                              <XCircle className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="font-semibold text-xs sm:text-sm text-[var(--text-color)]">No / Mistake</div>
                              <div className="text-[11px] text-[var(--sub-text-color)]">Not interested</div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* If Accurate -> Step 2 Qualification */}
                    {isRequirementAccurate ? (
                      <div className="space-y-2.5 pt-1">
                        {/* Buying Urgency */}
                        <div className="p-3 rounded-xl bg-white border border-[var(--sahi-border-color)] space-y-1.5 shadow-2xs">
                          <label className="block text-xs font-semibold text-[var(--text-color)] flex items-center gap-1.5">
                            <Flame className="h-3.5 w-3.5 text-sahi-orange" />
                            How soon do you plan to proceed? <span className="text-sahi-red">*</span>
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              "Immediate (1-3 Days)",
                              "This Month",
                              "Just Researching",
                            ].map((item) => (
                              <button
                                key={item}
                                type="button"
                                onClick={() => setBuyingUrgency(item)}
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95",
                                  buyingUrgency === item
                                    ? "bg-sahi-primary-color text-white border-sahi-primary-color font-semibold shadow-2xs"
                                    : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                )}
                              >
                                {item}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Preferred Contact Method */}
                        <div className="p-3 rounded-xl bg-white border border-[var(--sahi-border-color)] space-y-1.5 shadow-2xs">
                          <label className="block text-xs font-semibold text-[var(--text-color)] flex items-center gap-1.5">
                            <MessageSquare className="h-3.5 w-3.5 text-sahi-primary-color" />
                            Best way to reach you?
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { id: "WhatsApp", icon: BsWhatsapp },
                              { id: "Phone Call", icon: Phone },
                              { id: "Email", icon: Mail },
                            ].map((channel) => {
                              const Icon = channel.icon;
                              const isSel = preferredContact === channel.id;
                              return (
                                <button
                                  key={channel.id}
                                  type="button"
                                  onClick={() => setPreferredContact(channel.id)}
                                  className={cn(
                                    "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5 active:scale-95",
                                    isSel
                                      ? "bg-sahi-primary-color text-white border-sahi-primary-color font-semibold shadow-2xs"
                                      : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                  )}
                                >
                                  <Icon className="h-3.5 w-3.5" />
                                  <span>{channel.id}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Expected Quantity / Volume */}
                        <div className="p-3 rounded-xl bg-white border border-[var(--sahi-border-color)] space-y-1.5 shadow-2xs">
                          <label className="block text-xs font-semibold text-[var(--text-color)] flex items-center gap-1.5">
                            <Package className="h-3.5 w-3.5 text-sahi-primary-color" />
                            Expected Volume / Scale:
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              "Sample / Trial",
                              "Standard Batch",
                              "Bulk Order",
                              "Ongoing Supply",
                            ].map((vol) => (
                              <button
                                key={vol}
                                type="button"
                                onClick={() => setExpectedVolume(vol)}
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95",
                                  expectedVolume === vol
                                    ? "bg-sahi-primary-color text-white border-sahi-primary-color font-semibold shadow-2xs"
                                    : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                )}
                              >
                                {vol}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Inline Notes & Submit */}
                        {renderNotesAndSubmit(
                          buyingUrgency.startsWith("Immediate") ? QUICK_NOTES.QUALIFY_HOT : QUICK_NOTES.QUALIFY_WARM,
                          "Confirm & Submit Details"
                        )}
                      </div>
                    ) : (
                      /* If Not Interested -> Quick Reason */
                      <div className="p-3 rounded-xl bg-white border border-[var(--sahi-border-color)] space-y-3 shadow-2xs">
                        <div>
                          <label className="block text-xs font-semibold text-[var(--text-color)] mb-1.5">
                            Please select a reason:
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              "Inquired by mistake",
                              "Already purchased elsewhere",
                              "No longer needed",
                              "Wrong specifications",
                            ].map((rsn) => (
                              <button
                                key={rsn}
                                type="button"
                                onClick={() => setQualifyDropReason(rsn)}
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95",
                                  qualifyDropReason === rsn
                                    ? "bg-sahi-red text-white border-sahi-red font-semibold shadow-2xs"
                                    : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                )}
                              >
                                {rsn}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Inline Notes & Submit */}
                        {qualifyDropReason && renderNotesAndSubmit([], "Confirm & Submit Details")}
                      </div>
                    )}
                  </div>
                ) : (
                  /* ================= TOUCHPOINT 2: DECISION PULL (SahiCRM Look & Feel) ================= */
                  <div className="space-y-2.5">
                    <div className="flex flex-col gap-2.5">
                      {/* 1. Ready to Proceed */}
                      <div
                        className={cn(
                          "rounded-xl border transition-all duration-150 overflow-hidden",
                          decision === DECISION_TYPES.READY
                            ? "border-2 border-sahi-green bg-sahi-green-extra-light shadow-2xs"
                            : "border-[var(--sahi-border-color)] bg-white hover:border-gray-300 shadow-2xs"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setDecision(DECISION_TYPES.READY)}
                          className="w-full p-3 sm:p-3.5 text-left relative flex items-start gap-3 group active:scale-[0.99]"
                        >
                          <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                            decision === DECISION_TYPES.READY
                              ? "bg-sahi-green text-white"
                              : "bg-sahi-green-light text-sahi-green"
                          )}>
                            <Rocket className="w-4.5 h-4.5" />
                          </div>

                          <div className="flex-1 min-w-0 pr-6">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-[var(--text-color)]">Ready to Proceed</span>
                            </div>
                            <div className="text-xs text-[var(--sub-text-color)] mt-0.5 leading-relaxed">
                              Finalized — Ready to place order & start
                            </div>
                          </div>

                          <div className="absolute top-3.5 right-3.5">
                            <div className={cn(
                              "w-4.5 h-4.5 rounded-full flex items-center justify-center transition-all",
                              decision === DECISION_TYPES.READY
                                ? "bg-sahi-green text-white"
                                : "border-2 border-gray-300 bg-white"
                            )}>
                              {decision === DECISION_TYPES.READY && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </button>

                        {/* Inline Notes & Submit when Ready is selected */}
                        {decision === DECISION_TYPES.READY && (
                          <div className="px-3.5 pb-3.5 pt-1 border-t border-sahi-green/20 bg-white/60">
                            {renderNotesAndSubmit(QUICK_NOTES.READY, "Confirm & Proceed with Order")}
                          </div>
                        )}
                      </div>

                      {/* 2. Need More Time + Integrated Sub-Options */}
                      <div
                        className={cn(
                          "rounded-xl border transition-all duration-150 overflow-hidden",
                          decision === DECISION_TYPES.DELAY
                            ? "border-2 border-sahi-orange bg-sahi-orange-light/10 shadow-2xs"
                            : "border-[var(--sahi-border-color)] bg-white hover:border-gray-300 shadow-2xs"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setDecision(DECISION_TYPES.DELAY)}
                          className="w-full p-3 sm:p-3.5 text-left relative flex items-start gap-3 group active:scale-[0.99]"
                        >
                          <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                            decision === DECISION_TYPES.DELAY
                              ? "bg-sahi-orange text-white"
                              : "bg-sahi-orange-light text-sahi-orange"
                          )}>
                            <Hourglass className="w-4.5 h-4.5" />
                          </div>

                          <div className="flex-1 min-w-0 pr-6">
                            <span className="font-semibold text-sm text-[var(--text-color)]">Need More Time</span>
                            <div className="text-xs text-[var(--sub-text-color)] mt-0.5 leading-relaxed">
                              Evaluating / Revisit at a later date
                            </div>
                          </div>

                          <div className="absolute top-3.5 right-3.5">
                            <div className={cn(
                              "w-4.5 h-4.5 rounded-full flex items-center justify-center transition-all",
                              decision === DECISION_TYPES.DELAY
                                ? "bg-sahi-orange text-white"
                                : "border-2 border-gray-300 bg-white"
                            )}>
                              {decision === DECISION_TYPES.DELAY && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </button>

                        {/* When would be the best time sub-div */}
                        {decision === DECISION_TYPES.DELAY && (
                          <div className="px-3.5 pb-3.5 pt-2 border-t border-sahi-orange/20 bg-sahi-orange-light/5 space-y-3">
                            <div>
                              <label className="block text-xs font-semibold text-[var(--text-color)] flex items-center gap-1.5 mb-2">
                                <Clock className="w-3.5 h-3.5 text-sahi-orange" />
                                When should we reconnect with you? <span className="text-sahi-red">*</span>
                              </label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                  { label: "In 1 Week", icon: Zap },
                                  { label: "In 2 Weeks", icon: CalendarDays },
                                  { label: "Next Month", icon: CalendarIcon },
                                  { label: "Custom Date", icon: Pencil },
                                ].map((opt) => {
                                  const Icon = opt.icon;
                                  return (
                                    <button
                                      key={opt.label}
                                      type="button"
                                      onClick={() => setDelayTimeline(opt.label)}
                                      className={cn(
                                        "py-1.5 px-2.5 rounded-lg text-xs font-medium transition-all border flex items-center justify-center gap-1.5 active:scale-95",
                                        delayTimeline === opt.label
                                          ? "bg-sahi-orange text-white border-sahi-orange font-semibold shadow-2xs"
                                          : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                      )}
                                    >
                                      <Icon className="w-3.5 h-3.5 shrink-0" />
                                      <span>{opt.label}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              {delayTimeline === "Custom Date" && (
                                <div className="pt-2">
                                  <label className="block text-[11px] font-semibold text-[var(--text-color)] mb-1">
                                    Select Specific Date:
                                  </label>
                                  <input
                                    type="date"
                                    value={customDate}
                                    min={new Date().toISOString().split("T")[0]}
                                    onChange={(e) => setCustomDate(e.target.value)}
                                    className="w-full sm:w-auto px-2.5 py-1.5 border border-[var(--sahi-border-color)] rounded-lg text-xs sm:text-sm text-[var(--text-color)] bg-white focus:outline-none focus:border-sahi-orange shadow-2xs font-medium"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Show notes and submit inside Need More Time card after timeline is chosen */}
                            {delayTimeline && (delayTimeline !== "Custom Date" || customDate) && (
                              <div className="pt-1">
                                {renderNotesAndSubmit(QUICK_NOTES.DELAY, "Submit Decision")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 3. Not Proceeding + Integrated Sub-Options */}
                      <div
                        className={cn(
                          "rounded-xl border transition-all duration-150 overflow-hidden",
                          decision === DECISION_TYPES.DROP
                            ? "border-2 border-sahi-red bg-sahi-red-light/10 shadow-2xs"
                            : "border-[var(--sahi-border-color)] bg-white hover:border-gray-300 shadow-2xs"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setDecision(DECISION_TYPES.DROP)}
                          className="w-full p-3 sm:p-3.5 text-left relative flex items-start gap-3 group active:scale-[0.99]"
                        >
                          <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                            decision === DECISION_TYPES.DROP
                              ? "bg-sahi-red text-white"
                              : "bg-sahi-red-light text-sahi-red"
                          )}>
                            <XCircle className="w-4.5 h-4.5" />
                          </div>

                          <div className="flex-1 min-w-0 pr-6">
                            <span className="font-semibold text-sm text-[var(--text-color)]">Not Proceeding</span>
                            <div className="text-xs text-[var(--sub-text-color)] mt-0.5 leading-relaxed">
                              Decided not to move forward / Drop
                            </div>
                          </div>

                          <div className="absolute top-3.5 right-3.5">
                            <div className={cn(
                              "w-4.5 h-4.5 rounded-full flex items-center justify-center transition-all",
                              decision === DECISION_TYPES.DROP
                                ? "bg-sahi-red text-white"
                                : "border-2 border-gray-300 bg-white"
                            )}>
                              {decision === DECISION_TYPES.DROP && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </button>

                        {/* Sub-options for Card 3: Not Proceeding */}
                        {decision === DECISION_TYPES.DROP && (
                          <div className="px-3.5 pb-3.5 pt-2 border-t border-sahi-red/20 bg-sahi-red-light/5 space-y-3">
                            <div>
                              <label className="block text-xs font-semibold text-[var(--text-color)] mb-2">
                                Please let us know the primary reason: <span className="text-sahi-red">*</span>
                              </label>
                              <div className="flex flex-wrap gap-1.5">
                                {dropReasonOptions.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setDropReason(opt)}
                                    className={cn(
                                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95",
                                      dropReason === opt
                                        ? "bg-sahi-red text-white border-sahi-red font-semibold shadow-2xs"
                                        : "bg-white text-[var(--text-color)] border-[var(--sahi-border-color)] hover:bg-gray-50"
                                    )}
                                  >
                                    {opt}
                                  </button>
                                ))}
                              </div>

                              {(dropReason.toLowerCase().includes("competitor") || dropReason.toLowerCase().includes("bought from") || dropReason.toLowerCase() === "other") && (
                                <div className="pt-2">
                                  <label className="block text-[11px] font-semibold text-[var(--text-color)] mb-1">
                                    {dropReason.toLowerCase() === "other" ? "Please specify reason (Optional):" : "Competitor Name (Optional):"}
                                  </label>
                                  <input
                                    type="text"
                                    placeholder={dropReason.toLowerCase() === "other" ? "e.g. Budget constraints, project shelved..." : "e.g. Competitor name"}
                                    value={competitorName}
                                    onChange={(e) => setCompetitorName(e.target.value)}
                                    className="w-full px-3 py-1.5 border border-[var(--sahi-border-color)] rounded-lg text-xs sm:text-sm text-[var(--text-color)] bg-white focus:outline-none focus:border-sahi-red shadow-2xs font-medium"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Show notes and submit inside Not Proceeding card once reason is selected */}
                            {dropReason && (
                              <div className="pt-1">
                                {renderNotesAndSubmit(QUICK_NOTES.DROP, "Submit Decision")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      )}
      </main>
    </div>
  );
}
